import React, { useEffect, useMemo, useRef, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowModel,
  GridToolbarContainer,
} from '@mui/x-data-grid';

import LiveLockBanner from './LiveLockBanner';
import TranslationCatalogStatusChip from './TranslationCatalogStatusChip';
import {
  buildTranslationCatalogRows,
  formatNullableDateTime,
  getDefaultTranslationCatalogDescription,
  getTranslationLocales,
  isTranslationCatalogLocked,
  isTranslationCatalogStale,
  mapRowsToTranslationCatalogPatch,
} from './translationCatalogUtils';
import type { TranslationCatalog, TranslationCatalogRow } from './types';

interface GridTranslationCatalogRow {
  id: string;
  key: string;
  description: string;
  sourceValue: string;
  [locale: string]: string;
}

interface Props {
  catalog?: TranslationCatalog;
  isLoading: boolean;
  isDeletingCatalog: boolean;
  deletingFieldName?: string;
  canEdit: boolean;
  canDeleteField: boolean;
  isDirty: boolean;
  importedDraft?: {
    rows: Array<TranslationCatalogRow>;
    locales: Array<string>;
    hasDescriptionColumn: boolean;
    hasSourceValueColumn: boolean;
    nonce: number;
  };
  canDownloadCsv: boolean;
  canLoadCsv: boolean;
  onDownloadCsv: () => void;
  onImportCsv: (file: File) => void;
  onDeleteField: (fieldName: string) => void;
  onRowsChange: (rows: Array<TranslationCatalogRow>, locales: Array<string>, isDirty: boolean) => void;
}

interface AddFieldToolbarProps {
  canEdit: boolean;
  isLocked: boolean;
  newFieldName: string;
  newFieldError: string;
  onAddField: () => void;
  onFieldNameChange: (value: string) => void;
}

function parseFieldNames(value: string): Array<string> {
  return value
    .split(/[\n,]/)
    .map((fieldName) => fieldName.trim())
    .filter(Boolean);
}

function toGridRows(rows: Array<TranslationCatalogRow>, locales: Array<string>): Array<GridTranslationCatalogRow> {
  return rows.map((row) => {
    const localeValues = locales.reduce<Record<string, string>>((result, locale) => {
      result[locale] = row.translations[locale] ?? '';
      return result;
    }, {});

    return {
      id: row.id,
      key: row.key,
      description: row.description,
      sourceValue: row.sourceValue,
      ...localeValues,
    };
  });
}

function fromGridRows(rows: Array<GridTranslationCatalogRow>, locales: Array<string>): Array<TranslationCatalogRow> {
  return rows.map((row) => ({
    id: row.id,
    key: row.key,
    description: row.description ?? '',
    sourceValue: row.sourceValue ?? '',
    translations: locales.reduce<Record<string, string>>((result, locale) => {
      result[locale] = row[locale] ?? '';
      return result;
    }, {}),
  }));
}

function mergeImportedRows(
  currentRows: Array<TranslationCatalogRow>,
  currentLocales: Array<string>,
  importedRows: Array<TranslationCatalogRow>,
  importedLocales: Array<string>,
  hasDescriptionColumn: boolean,
  hasSourceValueColumn: boolean,
): { rows: Array<TranslationCatalogRow>; locales: Array<string> } {
  const locales = Array.from(new Set([...currentLocales, ...importedLocales])).sort((a, b) => a.localeCompare(b));
  const importedRowMap = new Map(importedRows.map((row) => [row.key, row]));
  const currentRowKeys = new Set(currentRows.map((row) => row.key));

  const mergeRow = (currentRow: TranslationCatalogRow | undefined, importedRow: TranslationCatalogRow) => ({
    id: importedRow.id,
    key: importedRow.key,
    description: hasDescriptionColumn ? importedRow.description : (currentRow?.description ?? importedRow.description),
    sourceValue: hasSourceValueColumn ? importedRow.sourceValue : (currentRow?.sourceValue ?? importedRow.sourceValue),
    translations: locales.reduce<Record<string, string>>((result, locale) => {
      result[locale] = importedLocales.includes(locale)
        ? (importedRow.translations[locale] ?? '')
        : (currentRow?.translations[locale] ?? '');
      return result;
    }, {}),
  });

  const rows = currentRows.map((currentRow) => {
    const importedRow = importedRowMap.get(currentRow.key);

    if (!importedRow) {
      return {
        ...currentRow,
        translations: locales.reduce<Record<string, string>>((result, locale) => {
          result[locale] = currentRow.translations[locale] ?? '';
          return result;
        }, {}),
      };
    }

    return mergeRow(currentRow, importedRow);
  });

  importedRows.forEach((importedRow) => {
    if (!currentRowKeys.has(importedRow.key)) {
      rows.push(mergeRow(undefined, importedRow));
    }
  });

  return { rows, locales };
}

export function mapEditorRowsToPatch(rows: Array<TranslationCatalogRow>, locales: Array<string>) {
  return mapRowsToTranslationCatalogPatch(rows, locales);
}

function getUserUrl(userId: string): string {
  return `${window.location.origin}/users/${userId}`;
}

function UserLink({ userId, label }: { userId: string; label: string }) {
  return (
    <Tooltip title={label}>
      <Link
        href={getUserUrl(userId)}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}
      >
        <PersonOutlineIcon fontSize="inherit" />
        <Typography component="span" variant="caption" fontWeight={700}>
          View profile
        </Typography>
      </Link>
    </Tooltip>
  );
}

function MultilineEditCell(params: GridRenderEditCellParams<GridTranslationCatalogRow, string>) {
  return (
    <TextField
      value={params.value ?? ''}
      onChange={(event) => {
        params.api.setEditCellValue({ id: params.id, field: params.field, value: event.target.value }, event);
      }}
      autoFocus
      multiline
      minRows={3}
      maxRows={8}
      fullWidth
      variant="standard"
      InputProps={{ disableUnderline: true }}
      sx={{
        px: 1,
        py: 0.75,
        '& textarea': {
          fontSize: (theme) => theme.typography.body2.fontSize,
          lineHeight: 1.45,
        },
      }}
    />
  );
}

function AddFieldToolbar({
  canEdit,
  isLocked,
  newFieldName,
  newFieldError,
  onAddField,
  onFieldNameChange,
}: AddFieldToolbarProps) {
  return (
    <GridToolbarContainer sx={{ px: 1, py: 1, borderBottom: 1, borderColor: 'divider' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        gap={1}
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        width="100%"
        maxWidth={620}
      >
        <TextField
          size="small"
          label="Add field"
          value={newFieldName}
          error={!!newFieldError}
          helperText={newFieldError || 'Use one field per line or comma-separated values.'}
          disabled={!canEdit || isLocked}
          multiline
          maxRows={4}
          onChange={(event) => onFieldNameChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onAddField();
            }
          }}
          sx={{ flex: 1 }}
        />
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          size="small"
          disabled={!canEdit || isLocked}
          onClick={onAddField}
          sx={{ minHeight: 40, whiteSpace: 'nowrap' }}
        >
          Add field
        </Button>
      </Stack>
    </GridToolbarContainer>
  );
}

const dataGridSlots = {
  toolbar: AddFieldToolbar,
} as unknown as NonNullable<React.ComponentProps<typeof DataGrid>['slots']>;

export default function TranslationCatalogEditor({
  catalog,
  isLoading,
  isDeletingCatalog,
  deletingFieldName,
  canEdit,
  canDeleteField,
  isDirty,
  importedDraft,
  canDownloadCsv,
  canLoadCsv,
  onDownloadCsv,
  onImportCsv,
  onDeleteField,
  onRowsChange,
}: Props) {
  const locales = useMemo(() => getTranslationLocales(catalog), [catalog]);
  const isLocked = isTranslationCatalogLocked(catalog);
  const isStale = isTranslationCatalogStale(catalog);
  const columnNames = useMemo(() => catalog?.columnNames ?? [], [catalog]);
  const [rows, setRows] = useState<Array<GridTranslationCatalogRow>>([]);
  const [activeLocales, setActiveLocales] = useState<Array<string>>([]);
  const [visibleLocales, setVisibleLocales] = useState<Array<string>>([]);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldError, setNewFieldError] = useState('');
  const catalogIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!catalog) {
      catalogIdRef.current = undefined;
      setRows([]);
      onRowsChange([], [], false);
      return;
    }

    const catalogIdChanged = catalog.id !== catalogIdRef.current;
    catalogIdRef.current = catalog.id;

    if (isDirty && !catalogIdChanged) {
      return;
    }

    const nextRows = toGridRows(buildTranslationCatalogRows(catalog), locales);
    setRows(nextRows);
    setActiveLocales(locales);
    setNewFieldName('');
    setNewFieldError('');
    setVisibleLocales((currentLocales) => currentLocales.filter((locale) => locales.includes(locale)));
    onRowsChange(fromGridRows(nextRows, locales), locales, false);
  }, [catalog, isDirty, locales, onRowsChange]);

  useEffect(() => {
    if (!importedDraft) {
      return;
    }

    const mergedDraft = mergeImportedRows(
      catalog ? buildTranslationCatalogRows(catalog) : [],
      catalog ? locales : [],
      importedDraft.rows,
      importedDraft.locales,
      importedDraft.hasDescriptionColumn,
      importedDraft.hasSourceValueColumn,
    );
    const nextRows = toGridRows(mergedDraft.rows, mergedDraft.locales);
    setRows(nextRows);
    setActiveLocales(mergedDraft.locales);
    setVisibleLocales(mergedDraft.locales);
    setNewFieldName('');
    setNewFieldError('');
    onRowsChange(mergedDraft.rows, mergedDraft.locales, true);
  }, [catalog, importedDraft, locales, onRowsChange]);

  const columns = useMemo<Array<GridColDef<GridTranslationCatalogRow>>>(() => {
    const editable = canEdit && !isLocked;
    const isFieldDeleteInFlight = !!deletingFieldName;
    const sharedEditableColumn: Partial<GridColDef<GridTranslationCatalogRow>> = {
      editable: editable && !isFieldDeleteInFlight && !isDeletingCatalog,
      minWidth: 280,
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GridTranslationCatalogRow, string>) => (
        <Typography variant="body2" sx={{ whiteSpace: 'normal', lineHeight: 1.4 }}>
          {params.value}
        </Typography>
      ),
      renderEditCell: MultilineEditCell,
    };

    return [
      {
        field: 'key',
        headerName: 'Key',
        width: 165,
        sortable: false,
        renderCell: (params) => (
          <Typography variant="body2" fontFamily="monospace" fontWeight={700} noWrap>
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        width: 52,
        getActions: (params) => [
          <GridActionsCellItem
            key="delete"
            icon={<DeleteOutlineIcon fontSize="small" />}
            label={`Delete ${params.row.key}`}
            disabled={!canDeleteField || rows.length <= 1 || isDeletingCatalog || isFieldDeleteInFlight}
            onClick={() => onDeleteField(params.row.key)}
            showInMenu={false}
          />,
        ],
      },
      {
        ...sharedEditableColumn,
        field: 'description',
        headerName: 'Description',
      },
      {
        ...sharedEditableColumn,
        field: 'sourceValue',
        headerName: 'en-AU',
      },
      ...visibleLocales.map<GridColDef<GridTranslationCatalogRow>>((locale) => ({
        ...sharedEditableColumn,
        field: locale,
        headerName: locale,
      })),
    ];
  }, [
    canDeleteField,
    canEdit,
    deletingFieldName,
    isDeletingCatalog,
    isLocked,
    onDeleteField,
    rows.length,
    visibleLocales,
  ]);

  const processRowUpdate = (newRow: GridRowModel<GridTranslationCatalogRow>) => {
    const updatedRow = newRow as GridTranslationCatalogRow;
    const nextRows = rows.map((row) => (row.id === updatedRow.id ? updatedRow : row));
    setRows(nextRows);
    onRowsChange(fromGridRows(nextRows, activeLocales), activeLocales, true);
    return updatedRow;
  };

  const toggleLocale = (locale: string) => {
    setVisibleLocales((currentLocales) =>
      currentLocales.includes(locale)
        ? currentLocales.filter((currentLocale) => currentLocale !== locale)
        : [...currentLocales, locale],
    );
  };

  const handleAddField = () => {
    const fieldNames = parseFieldNames(newFieldName);

    if (fieldNames.length === 0) {
      setNewFieldError('Enter at least one field key');
      return;
    }

    const invalidFieldName = fieldNames.find((fieldName) => !/^[A-Za-z0-9_]+$/.test(fieldName));
    if (invalidFieldName) {
      setNewFieldError(`Use letters, numbers, and underscores only: ${invalidFieldName}`);
      return;
    }

    const duplicateInputFieldName = fieldNames.find((fieldName, index) => fieldNames.indexOf(fieldName) !== index);
    if (duplicateInputFieldName) {
      setNewFieldError(`Field is listed more than once: ${duplicateInputFieldName}`);
      return;
    }

    const existingFieldName = fieldNames.find((fieldName) => rows.some((row) => row.key === fieldName));
    if (existingFieldName) {
      setNewFieldError(`Field already exists: ${existingFieldName}`);
      return;
    }

    const newRows = fieldNames.map<GridTranslationCatalogRow>((fieldName) => ({
      id: fieldName,
      key: fieldName,
      description: getDefaultTranslationCatalogDescription(fieldName),
      sourceValue: '',
      ...activeLocales.reduce<Record<string, string>>((result, locale) => {
        result[locale] = '';
        return result;
      }, {}),
    }));
    const nextRows = [...rows, ...newRows];
    setRows(nextRows);
    setNewFieldName('');
    setNewFieldError('');
    onRowsChange(fromGridRows(nextRows, activeLocales), activeLocales, true);
  };

  if (!catalog && !isLoading) {
    return (
      <Stack height="calc(100vh - 210px)" minHeight={520} justifyContent="center" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Select a catalog to review translations.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack
      spacing={2}
      minWidth={0}
      sx={{
        opacity: isDeletingCatalog ? 0.55 : 1,
        pointerEvents: isDeletingCatalog ? 'none' : 'auto',
        transition: 'opacity 120ms ease',
      }}
    >
      {isLoading && <LinearProgress />}
      {catalog && (
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            justifyContent="space-between"
            alignItems="flex-start"
            gap={1.5}
          >
            <Stack spacing={0.5} minWidth={0}>
              <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="h5" fontWeight={700} noWrap>
                  {catalog.catalogName}
                </Typography>
                <TranslationCatalogStatusChip status={catalog.status} />
                <Chip size="small" label={catalog.brand} variant="outlined" />
                <Tooltip
                  title={
                    canLoadCsv
                      ? 'Load data from a CSV file into this table'
                      : 'Save or discard changes before loading a CSV into this table'
                  }
                >
                  <span>
                    <IconButton
                      component="label"
                      aria-label="Load CSV into table"
                      size="small"
                      disabled={!canLoadCsv}
                      color="primary"
                      sx={{ width: 28, height: 28 }}
                    >
                      <FileUploadOutlinedIcon fontSize="small" />
                      <input
                        hidden
                        type="file"
                        accept=".csv,text/csv"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          event.target.value = '';
                          if (file) {
                            onImportCsv(file);
                          }
                        }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={canDownloadCsv ? 'Download the data from this table as CSV' : 'Select a catalog to download'}
                >
                  <span>
                    <IconButton
                      aria-label="Download table CSV"
                      size="small"
                      disabled={!canDownloadCsv}
                      color="primary"
                      onClick={onDownloadCsv}
                      sx={{ width: 28, height: 28 }}
                    >
                      <DownloadOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
              <Typography variant="body2" color="text.secondary" noWrap>
                {catalog.id}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              gap={1.5}
              flexWrap="wrap"
              alignItems="center"
              justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
              sx={{
                ml: { lg: 'auto' },
                minWidth: 0,
                whiteSpace: 'nowrap',
                textAlign: { lg: 'right' },
              }}
            >
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Typography variant="caption" color="text.secondary">
                  Updated {formatNullableDateTime(catalog.updatedAt)}
                </Typography>
              </Stack>
              {catalog.lockedAt && (
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <LockOutlinedIcon fontSize="inherit" color="success" />
                  <Typography variant="caption" color="text.secondary">
                    {formatNullableDateTime(catalog.lockedAt)}
                  </Typography>
                </Stack>
              )}
              {catalog.lockedBy && <UserLink userId={catalog.lockedBy} label="View locked by user" />}
              {catalog.unlockedBy && <UserLink userId={catalog.unlockedBy} label="View unlocked by user" />}
              <Typography variant="caption" color="text.secondary">
                Translated {formatNullableDateTime(catalog.translatedAt)}
              </Typography>
            </Stack>
          </Stack>
          {isLocked && <LiveLockBanner catalog={catalog} />}
          <Divider />
          {!canEdit && <Alert severity="info">You can view this catalog. Mutations require hero-planner-admin.</Alert>}
          {isDeletingCatalog && <Alert severity="info">Deleting this catalog from Braze and admin...</Alert>}
          {deletingFieldName && (
            <Alert severity="info">Deleting {deletingFieldName} from Braze and this catalog...</Alert>
          )}
          {isStale && (
            <Alert severity="warning" sx={{ alignItems: 'center' }}>
              This catalog was updated after it was translated. Manual overrides are okay if intentional; otherwise
              regenerate translations. Regenerating will replace previous manual overrides with auto-translated values.
            </Alert>
          )}
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            <Chip
              size="small"
              label={`${columnNames.length} fields`}
              sx={{
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: 'primary.dark',
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.28),
              }}
              variant="outlined"
            />
            <Chip
              size="small"
              label={`${locales.length} translated languages`}
              sx={{
                bgcolor: (theme) => alpha(theme.palette.info.main, 0.1),
                color: 'info.dark',
                borderColor: (theme) => alpha(theme.palette.info.main, 0.28),
              }}
              variant="outlined"
            />
            {locales.map((locale) => {
              const isVisible = visibleLocales.includes(locale);
              return (
                <Chip
                  key={locale}
                  size="small"
                  label={locale}
                  onClick={() => toggleLocale(locale)}
                  color={isVisible ? 'info' : 'default'}
                  variant={isVisible ? 'filled' : 'outlined'}
                  sx={{
                    bgcolor: isVisible ? undefined : (theme) => alpha(theme.palette.info.main, 0.04),
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.32),
                  }}
                />
              );
            })}
          </Stack>
          <Box sx={{ height: 'calc(100vh - 355px)', minHeight: 430, position: 'relative' }}>
            {(isDeletingCatalog || deletingFieldName) && (
              <LinearProgress
                sx={{
                  position: 'absolute',
                  zIndex: 2,
                  top: 0,
                  left: 0,
                  right: 0,
                }}
              />
            )}
            <DataGrid
              rows={rows}
              columns={columns}
              loading={isLoading || !!deletingFieldName}
              slots={dataGridSlots}
              slotProps={{
                toolbar: {
                  canEdit,
                  isLocked,
                  newFieldName,
                  newFieldError,
                  onAddField: handleAddField,
                  onFieldNameChange: (value: string) => {
                    setNewFieldName(value);
                    if (newFieldError) setNewFieldError('');
                  },
                } as unknown as NonNullable<React.ComponentProps<typeof DataGrid>['slotProps']>['toolbar'],
              }}
              processRowUpdate={processRowUpdate}
              disableRowSelectionOnClick
              hideFooterSelectedRowCount
              getRowHeight={() => 'auto'}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 50, page: 0 },
                },
              }}
              pageSizeOptions={[25, 50, 100]}
              sx={{
                border: 0,
                bgcolor: isLocked ? 'action.disabledBackground' : 'background.paper',
                '& .MuiDataGrid-cell': {
                  py: 1.25,
                  alignItems: 'flex-start',
                },
                '& .MuiDataGrid-cell--editable': {
                  bgcolor: isLocked ? 'action.disabledBackground' : 'background.default',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 700,
                },
                '& .MuiDataGrid-row[data-id="sourceValue"]': {
                  bgcolor: (theme) =>
                    isLocked ? theme.palette.action.disabledBackground : alpha(theme.palette.primary.main, 0.06),
                },
              }}
            />
          </Box>
        </Stack>
      )}
    </Stack>
  );
}
